import * as React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import {
  MainInfo,
  FreeCourses,
  Program,
  TargetAudience,
  Benefit,
  Author,
  Cost,
  Teachers,
  FrequentlyQuestions,
  Footer,
} from './blocks';
import {
  CoursePreview,
  DiscountBanner,
} from './components';
import {CoursePromoProvider as Provider} from './context';
import {transition} from 'helpers/css';
import {CourseObject, getCoursesByIDsArray} from 'api/course';
import {getGiftCourses, getPromoData} from './helpers';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';

const MainInfoBox = styled.div`
  min-height: 570px;
  margin-top: -20px;
`;

const Block = styled.div`
  margin-top: 80px;

  @media screen and (max-width: 640px) {
    margin-top: 40px;
  }
`;

const PreviewWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  z-index: -1;
  
  @media screen and (max-width: 640px) {
    position: relative;
    margin-bottom: -80px;
  }
`;

const Container = styled.div<{
  isDiscountShowed: boolean
}>`
  max-width: 100vw;
  overflow: hidden;
  ${transition()};
  
  padding-bottom: ${({isDiscountShowed}) => (
    isDiscountShowed
      ? '70px'
      : 0
  )};

  @media screen and (max-width: 640px) {
    padding-bottom: 0;
  }
`;

const Content = styled.div`
  display: grid;
  gap: 80px;
  margin: 80px auto;
`;

const Box = styled.div`
  display: grid;
  gap: 80px;
  width: 100%;
  margin: 0 auto;
  max-width: 1280px;
`;

const CostContainer = styled(Block)`
  margin: 0;
  display: flex;
  justify-content: center;
  padding: 80px 0;
  background: ${({theme}) => `
    ${theme.variables.accentBackgroundColor};
  `};

  @media screen and (max-width: 640px) {
    padding: 40px 0;
  }
`;

const DiscountBannerContainer = styled(Block)`
  margin: 0;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  @media screen and (max-width: 640px) {
    display: none;
  }
`;

const FooterContainer = styled(CostContainer)`
  padding: 40px 15px;
`;

/**
 * The course promo component
 * @param {any} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function CoursePromo(
    props: CourseObject,
): React.ReactElement {
  const [data, setData] = React.useState(
      React.useMemo(
          () => getPromoData(props), [props],
      ),
  );

  /**
   * the function fetch and set free courses
   */
  function fetchAndSetGiftCourses() {
    if (props.freeCourses?.active && props.freeCourses?.list) {
      getCoursesByIDsArray(props.freeCourses?.list)
          .then((responce) => {
            validateResponse(responce);
            if (responce && !isError(responce)) {
              setData({
                ...data,
                giftCourses: getGiftCourses(responce),
              });
            }
          });
    }
  }

  React.useEffect(fetchAndSetGiftCourses, []);

  const [isBannerClosed, setBannerClosed] = React.useState(false);
  const [isDiscountShowed, setDiscountShowed] = React.useState(false);

  /**
   * hide banner if discount is not available
   * @return {void}
   */
  function checkIsDiscountAvailable() {
    const endDate = data.prices.discountEndDate || moment();
    const isNotOver = moment().diff(endDate) < 0;
    const discountAvailable = Boolean(
        data.prices.isInstallmentAvailable
        && isNotOver,
    );
    setDiscountShowed(discountAvailable);
  }

  /**
   * close discount banner
   * @return {void}
   */
  function closeBanner() {
    setDiscountShowed(false);
    setBannerClosed(true);
  }

  React.useEffect(() => {
    checkIsDiscountAvailable();
    const interval = setInterval(checkIsDiscountAvailable, 1000);
    isBannerClosed && clearInterval(interval);

    return () => clearInterval(interval);
  }, [isBannerClosed]);

  /**
   * render discount banner
   * @return {void}
   */
  function renderBanner() {
    if (isDiscountShowed && !isBannerClosed) {
      return (
        <DiscountBannerContainer>
          <DiscountBanner onClose={closeBanner}/>
        </DiscountBannerContainer>
      );
    }
  }

  function renderMainInfo() {
    return (
      <MainInfoBox>
        <MainInfo />
      </MainInfoBox>
    )
  }

  function renderFreeCourses() {
    if (data.giftCourses?.items.length) {
      return <FreeCourses />;
    }
  }

  function renderProgram() {
    if (data.chapters?.length) {
      return <Program />;
    }
  }

  function renderTargetAudience() {
    if (data.targetAudience.items.length) {
      return <TargetAudience />;
    }
  }

  function renderBenefit() {
    if (data.benefit.items.length) {
      return <Benefit/>;
    }
  }

  function renderAuthor() {
    const shouldRender = data.author.name
    || data.author.message
    || data.author.preview
    || data.author.role
    || data.author.video;
    
    if (shouldRender) {
      return <Author/>;
    }
  }

  function renderCost() {
    return (
      <CostContainer>
        <Cost />
      </CostContainer>
    )
  }

  function renderTeachersAndFAQ() {
    const shouldRender = data.teachers.length || data.questions.items.length;

    if (shouldRender) {
      return (
        <Box>
          {renderTeachers()}
          {renderFrequentlyQuestions()}
        </Box>
      )
    }
  }

  function renderTeachers() {
    if (data.teachers.length) {
      return <Teachers/>;
    }
  }

  function renderFrequentlyQuestions() {
    if (data.questions.items.length) {
      return <FrequentlyQuestions />;
    }
  }

  return (
    <Provider value={data}>
      <Container isDiscountShowed={isDiscountShowed && !isBannerClosed}>
        <PreviewWrapper>
          <CoursePreview />
        </PreviewWrapper>
        <Content>
          <Box>
            {renderMainInfo()}
            {renderFreeCourses()}
            {renderProgram()}
            {renderTargetAudience()}
            {renderBenefit()}
            {renderAuthor()}
          </Box>
          {renderCost()}
          {renderTeachersAndFAQ()}
        </Content>
        <FooterContainer>
          <Footer />
        </FooterContainer>
        {renderBanner()}
      </Container>
    </Provider>
  );
}
